import type { NarrativeInvalidation } from "@xhalo/story-contracts";
import { AlertTriangle, BookOpenText, CalendarClock, GitFork, Lightbulb, UsersRound } from "lucide-react";
import { ModuleGuide } from "./ModuleGuide";

import type { WorldState } from "../api/client";

type WorldPanelProps = {
  world: WorldState;
  invalidations: NarrativeInvalidation[];
  onResolve: (id: string) => void;
};

function text(value: unknown, fallback: string): string {
  return typeof value === "string" && value.length > 0 ? value : fallback;
}

export function WorldPanel({ world, invalidations, onResolve }: WorldPanelProps) {
  const open = invalidations.filter((item) => item.status === "open" || item.status === "acknowledged");
  return (
    <main className="world-panel" aria-label="世界与连续性">
      <header className="world-header">
        <div><span className="eyebrow">CANONICAL WORLD</span><h1>世界与连续性</h1></div>
        <p>所有条目均来自已接受的 Canonical Commit；Draft 不会出现在这里。</p>
      </header>
      <ModuleGuide module="world" />
      <section className="world-summary" aria-label="世界状态摘要">
        <article><BookOpenText /><strong>{world.facts.length}</strong><span>事实</span></article>
        <article><CalendarClock /><strong>{world.events.length}</strong><span>事件</span></article>
        <article><Lightbulb /><strong>{world.beliefs.length}</strong><span>信念</span></article>
        <article><UsersRound /><strong>{world.relationships.length}</strong><span>关系</span></article>
      </section>
      <div className="world-columns">
        <section className="world-card">
          <h2><BookOpenText size={18} />Canon 事实</h2>
          {world.facts.length > 0 ? world.facts.map((fact, index) => (
            <div className="world-row" key={text(fact.id, `fact-${index}`)}>
              <strong>{text(fact.subject_id, "未知主体")}</strong>
              <span>{text(fact.predicate, "关联")}</span>
              <p>{JSON.stringify(fact.object ?? null)}</p>
            </div>
          )) : <p className="empty-world">接受带有 NarrativeDelta 的变更后，事实会显示在这里。</p>}
        </section>
        <section className="world-card">
          <h2><GitFork size={18} />关系与承诺</h2>
          {world.relationships.map((relationship, index) => (
            <div className="world-row" key={text(relationship.id, `relationship-${index}`)}>
              <strong>{text(relationship.source_entity_id, "未知实体")}</strong>
              <span>{text(relationship.relationship_type, "关联")}</span>
              <p>{text(relationship.target_entity_id, "未知实体")}</p>
            </div>
          ))}
          {world.promises.map((promise, index) => (
            <div className="world-row" key={`${text(promise.id, `promise-${index}`)}:${text(promise.source_commit_id, "commit")}`}>
              <strong>{text(promise.id, "未命名承诺")}</strong>
              <span>{text(promise.status, "unknown")}</span>
              <p>{text(promise.summary, "无摘要")}</p>
            </div>
          ))}
          {world.relationships.length + world.promises.length === 0 ? <p className="empty-world">尚无关系或叙事承诺。</p> : null}
        </section>
        <section className="world-card invalidation-card">
          <h2><AlertTriangle size={18} />下游失效</h2>
          {open.length > 0 ? open.map((item) => (
            <div className="invalidation-item" key={item.id}>
              <span>{item.dependencyKind}</span>
              <strong>{item.changedUnitId.slice(0, 12)} → {item.affectedUnitId.slice(0, 12)}</strong>
              <p>{item.reason}</p>
              <button type="button" onClick={() => onResolve(item.id)}>标记已复核</button>
            </div>
          )) : <p className="empty-world">当前没有需要复核的下游场景。</p>}
        </section>
      </div>
    </main>
  );
}
